import { BookOpen, Database, RefreshCcw } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '~/components/ui/Accordion';
import ConnectorSetupGuide, {
  type SetupGuideData,
} from './ConnectorSetupGuide';
import ConnectorDataCatalogue, {
  type CatalogueTable,
} from './ConnectorDataCatalogue';

interface ConnectorDocumentationProps {
  setupGuide: SetupGuideData;
  reauthGuide?: SetupGuideData;
  catalogue?: CatalogueTable[];
  catalogueDescription?: string;
}

export default function ConnectorDocumentation({
  setupGuide,
  reauthGuide,
  catalogue,
  catalogueDescription = 'Explore tables and metrics to be synced',
}: ConnectorDocumentationProps) {
  return (
    <Accordion
      type="single"
      collapsible
      defaultValue="guide"
      className="w-full"
    >
      <AccordionItem value="guide" className="border-b">
        <AccordionTrigger className="hover:no-underline py-3">
          <div className="flex items-center gap-2.5 text-left">
            <div className="size-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <BookOpen className="size-5 text-primary" />
            </div>
            <div>
              <h4 className="font-semibold text-base">Setup Guide</h4>
              <p className="text-xs text-muted-foreground font-normal">
                Follow these steps to connect your account
              </p>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <ConnectorSetupGuide setupGuide={setupGuide} />
        </AccordionContent>
      </AccordionItem>

      {reauthGuide && (
        <AccordionItem value="reauth" className="border-b">
          <AccordionTrigger className="hover:no-underline py-3">
            <div className="flex items-center gap-2.5 text-left">
              <div className="size-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <RefreshCcw className="size-5 text-primary" />
              </div>
              <div>
                <h4 className="font-semibold text-base">Re-auth Guide</h4>
                <p className="text-xs text-muted-foreground font-normal">
                  Steps to refresh your connection
                </p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <ConnectorSetupGuide setupGuide={reauthGuide} />
          </AccordionContent>
        </AccordionItem>
      )}

      {catalogue && (
        <AccordionItem value="catalogue" className="border-b last:border-0">
          <AccordionTrigger className="hover:no-underline py-3">
            <div className="flex items-center gap-2.5 text-left">
              <div className="size-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Database className="size-5 text-primary" />
              </div>
              <div>
                <h4 className="font-semibold text-base">Data Catalogue</h4>
                <p className="text-xs text-muted-foreground font-normal">
                  {catalogueDescription}
                </p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <ConnectorDataCatalogue catalogue={catalogue} />
          </AccordionContent>
        </AccordionItem>
      )}
    </Accordion>
  );
}
